import { inject } from '@angular/core';
import { RoutingService } from '@skylab-angular-shared/core';
import { FilterChipsOptions } from '@skylab-angular-shared/ui';
import { ConfigType, ViewSettingsCache, ViewSettingsRepositoryBase } from '@skylab-shared/business';
import { DataFormats } from '@skylab-shared/constants';
import { isUndefined, SerializationUtils } from '@skylab-shared/utils';

export type ServiceFiltersConfig<TParams> = FilterChipsOptions & {
    initialFilterValues: TParams;
};

export abstract class FiltersOptionsServiceBase<TParams extends Object> {
    protected abstract viewSettingsKey: ConfigType;
    protected abstract get defaultFilterValues(): TParams;
    protected abstract get keysToReadFromViewSettings(): (keyof TParams)[];

    protected filterValues!: TParams;

    protected savedData!: TParams;

    private touched = false;

    private routingService = inject(RoutingService);

    protected constructor(
        private readonly viewSettingsRepository: ViewSettingsRepositoryBase
    ) { }

    public onFilter(filterFunction: (params: TParams) => void): void {
        filterFunction(this.filterValues);

        const data = SerializationUtils.serialize(this.filterValues, { dateFormat: DataFormats.date.webApi });
        this.viewSettingsRepository.saveViewSettingsAsync(this.viewSettingsKey, data);
        this.touched = true;
    }

    protected async initFilterValuesAsync(): Promise<TParams> {
        const isCacheKey = ViewSettingsCache.cache.get(this.viewSettingsKey);
        const plainData = await this.viewSettingsRepository.getViewSettingsAsync<TParams>(this.viewSettingsKey);
        this.savedData = SerializationUtils.deserialize(JSON.stringify(plainData), { dateFormat: DataFormats.date.webApi });
        const entryParams = this.viewEntryFilterParams();

        if (entryParams) {
            return {
                ...Object.fromEntries(Object.keys(this.defaultFilterValues).map(k => [k, null])),
                ...entryParams
            }
        }

        if (!this.savedData)
            return this.defaultFilterValues;

        if (isCacheKey && this.touched)
            return this.savedData;

        const params = this.defaultFilterValues;
        for (const x of this.keysToReadFromViewSettings) {
            if (!isUndefined(this.savedData[x])) params[x] = this.savedData[x];
        }

        return params;
    }

    private viewEntryFilterParams(): TParams | null {
        try {
            const routeParams: TParams = SerializationUtils.deserialize(JSON.stringify(this.routingService?.currentState?.params || {}), { dateFormat: DataFormats.date.webApi });

            const filtered = Object.fromEntries(
                Object.entries(routeParams).filter(([key]) => key in this.defaultFilterValues)
            ) as TParams

            const hasSome =  Object.keys(filtered).length > 0;
            if (!hasSome) return null;

            return filtered;
        } catch (err) {
            return null;
        }
    }

}
